import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UIValidationError } from 'icreate/common/validators';
import { normalizeLeadingZeros } from '@models/channel-sheet';

export enum InputType {
    PASSWORD = 'password',
    TEXT = 'text',
}

export enum InputValueState {
    error,
    normal,
    init,
}

type InputValidationEvent = {
    state: InputValueState;
    message: string;
    errorExtraInfo: any;
};

@Component({
    selector: 'app-channel-text-input',
    templateUrl: './channel-text-input.component.html',
    styleUrls: ['./channel-text-input.component.scss'],
    standalone: false,
})
export class ChannelTextInputComponent implements OnInit, OnChanges {
    readonly ERROR = InputValueState.error;
    readonly NORMAL = InputValueState.normal;
    readonly INIT = InputValueState.init;

    readonly INPUT_TYPE_PASSWORD = InputType.PASSWORD;
    readonly INPUT_TYPE_TEXT = InputType.TEXT;

    @Input() name: string;
    @Input() label: string;
    @Input() value: string;
    @Input() type: string = this.INPUT_TYPE_TEXT;
    @Input() unit: string = '';
    @Input() placeholder: string;
    @Input() maxLength: number = 50;
    @Input() passwordIconShow: boolean = false;
    @Input() inputError: UIValidationError | null = null;
    @Input() showInitialError: boolean = false;

    @Input() hasCounter: boolean = false;
    @Input() counterMax: number = 16;
    @Input() isDisabled: boolean = false;
    @Input() icon: string;
    @Input() dark: boolean = false;

    @Input() inputValueValidation: (value: any) => InputValidationEvent;
    @Input() inputValidation: InputValidationEvent;
    @Input() stripLeadingZeros: boolean = false;
    @Input() noLabel: boolean = false;

    @Output() onInputChange = new EventEmitter<{ name: string; value: string }>();
    @Output() onFocusChange = new EventEmitter<{ name: string; value: string }>();
    @Output() onInputKeyDown = new EventEmitter<{ event: KeyboardEvent; value: string }>();

    inputValue = '';
    focus = false;
    inputValueCheck = this.INIT;

    constructor(private cdr: ChangeDetectorRef) {}

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent) {
        if (this.focus) {
            this.onFocusChange.emit();
        }
    }

    ngOnInit() {
        this.inputValue = this.value !== null && this.value !== undefined ? String(this.value) : '';
        this.inputValueCheck = this.NORMAL;
        if (this.inputValue.length > 0 && this.showInitialError) {
            this.inputValueCheck = this.inputError?.message ? this.ERROR : this.NORMAL;
        }
        if (!this.label) this.noLabel = true;
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.inputValue !== '' && this.inputValue !== null && changes['inputError']) {
            this.inputValueCheck = this.inputError?.message ? this.ERROR : this.NORMAL;
        }

        if (changes['value']) {
            const newVal = String(this.value ?? '');
            if (!this.focus) {
                this.inputValue = newVal;
            }
            this.onCheckInputValue(this.inputValue);
        }

        if (changes['isDisabled'] && !changes['value']) {
            this.onCheckInputValue(this.inputValue);
        }

        this.cdr.detectChanges();
    }

    inputFocus() {
        this.focus = true;
    }

    inputBlur() {
        this.focus = false;
        if (this.stripLeadingZeros) {
            const normalized = normalizeLeadingZeros(this.inputValue);
            if (normalized !== this.inputValue) {
                this.inputValue = normalized;
                this.onInputChange.emit({ name: this.name, value: normalized });
            }
        }
        this.onFocusChange.emit({ name: this.name, value: this.inputValue });
        this.onCheckInputValue(this.inputValue);
    }

    onChange(value: string) {
        if (this.isDisabled) return;
        this.inputValue = value;
        this.onInputChange.emit({ name: this.name, value: value });
        this.onCheckInputValue(this.inputValue);
    }

    onCheckInputValue(value: string) {
        if (this.inputValueValidation) {
            const { state, message, errorExtraInfo } = this.inputValueValidation(value);
            if (state === this.ERROR) {
                this.inputError = {
                    message: message,
                    params: errorExtraInfo || {},
                };
            } else {
                this.inputError = null;
            }

            this.inputValueCheck = state;
        } else if (this.inputValidation) {
            const { state, message, errorExtraInfo } = this.inputValidation;
            if (state === this.ERROR) {
                this.inputError = {
                    message: message,
                    params: errorExtraInfo || {},
                };
            } else {
                this.inputError = null;
            }

            this.inputValueCheck = state;
        } else {
            if (this.inputError?.message) {
                this.inputValueCheck = this.ERROR;
            } else {
                this.inputValueCheck = this.NORMAL;
            }
        }
        this.cdr.detectChanges();
    }

    passwordModeChange() {
        if (this.type === InputType.PASSWORD) {
            this.type = this.INPUT_TYPE_TEXT;
        } else if (this.type === InputType.TEXT) {
            this.type = this.INPUT_TYPE_PASSWORD;
        }
    }

    onWheel(event: WheelEvent) {
        const scrollContainer = this.getScrollContainer();
        if (scrollContainer) {
            scrollContainer.scrollTop += event.deltaY;
            event.preventDefault();
        }
    }

    private getScrollContainer(): HTMLElement | null {
        let element = this.getInput()?.parentElement;
        while (element) {
            const overflowY = window.getComputedStyle(element).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return element;
            }
            element = element.parentElement;
        }
        return null;
    }

    private getInput(): HTMLInputElement | null {
        return document.querySelector(`input[name="${this.name}"]`);
    }

    inputKeyDown(event: KeyboardEvent) {
        if (this.isDisabled) return;
        this.onInputKeyDown?.emit({ event, value: this.inputValue });
    }

    get hasError() {
        return this.inputValueCheck === this.ERROR;
    }

    revalidate() {
        this.onCheckInputValue(this.inputValue);
        this.cdr.detectChanges();
    }
}
